import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  CloudUpload,
  Search,
  Trash2,
  Pencil,
  FileText,
  Folder,
  BarChart2,
  CheckCircle2,
  AlertCircle,
  Clock,
  HardDrive,
  Layers,
  TrendingUp,
  Loader2,
  Bot,
  Sparkles,
} from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardAction,
  CardFooter,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../components/ui/use-toast';

// ─── Types ────────────────────────────────────────────────────────────────────
interface KBDocument {
  id: number;
  title: string;
  file_name: string;
  file_type: string;
  file_size: number;
  status: 'processing' | 'indexed' | 'failed';
  error_message?: string;
  created_at: string;
  indexed_at?: string;
}

interface KBCategory {
  id: number;
  name: string;
  description?: string;
  color: string;
  icon: string;
  created_at: string;
}

interface SearchResult {
  chunk_id: number;
  content: string;
  document_title: string;
  document_id: number;
  file_name: string;
  similarity_score: number;
}

interface KBAnalytics {
  document_stats: Record<string, number>;
  total_chunks: number;
  total_size_bytes: number;
  total_size_mb: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const STATUS_BADGE: Record<string, string> = {
  indexed: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  processing: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300',
  failed: 'border-red-200 bg-red-50 text-reded-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300',
};

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'indexed') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
  if (status === 'processing') return <Clock className="h-4 w-4 text-amber-500 animate-spin" />;
  return <AlertCircle className="h-4 w-4 text-red-500" />;
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const KnowledgeBasePage: React.FC = () => {
  const [documents, setDocuments] = useState<KBDocument[]>([]);
  const [categories, setCategories] = useState<KBCategory[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [analytics, setAnalytics] = useState<KBAnalytics | null>(null);
  const [loading, setLoading] = useState(false);

  // Dialog/alert state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [editCategoryOpen, setEditCategoryOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<KBDocument | null>(null);
  const [catToDelete, setCatToDelete] = useState<KBCategory | null>(null);

  // Upload form
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  // Category forms
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [editingCategory, setEditingCategory] = useState<KBCategory | null>(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryDescription, setEditCategoryDescription] = useState('');

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);

  // ── Data loaders ────────────────────────────────────────────────────────────
  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/knowledge-base/documents');
      setDocuments(res.data.documents);
    } catch {
      toast({ title: 'Failed to load documents', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get('/knowledge-base/categories');
      setCategories(res.data.categories);
    } catch {
      toast({ title: 'Failed to load categories', variant: 'destructive' });
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const res = await api.get('/knowledge-base/analytics');
      setAnalytics(res.data);
    } catch {
      toast({ title: 'Failed to load analytics', variant: 'destructive' });
    }
  }, []);

  useEffect(() => {
    loadDocuments();
    loadCategories();
    loadAnalytics();
  }, [loadDocuments, loadCategories, loadAnalytics]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleFileUpload = async () => {
    if (!selectedFile || !uploadTitle) {
      toast({ title: 'Please select a file and enter a title', variant: 'destructive' });
      return;
    }
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadTitle);
      if (selectedCategories.length > 0) {
        formData.append('category_ids', JSON.stringify(selectedCategories));
      }
      await api.post('/knowledge-base/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Document uploaded successfully' });
      setUploadOpen(false);
      setSelectedFile(null);
      setUploadTitle('');
      setSelectedCategories([]);
      loadDocuments();
      loadAnalytics();
    } catch {
      toast({ title: 'Failed to upload document', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocumentConfirm = async () => {
    if (!docToDelete) return;
    try {
      await api.delete(`/knowledge-base/documents/${docToDelete.id}`);
      toast({ title: 'Document deleted successfully' });
      loadDocuments();
      loadAnalytics();
    } catch {
      toast({ title: 'Failed to delete document', variant: 'destructive' });
    } finally {
      setDocToDelete(null);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName) {
      toast({ title: 'Category name is required', variant: 'destructive' });
      return;
    }
    try {
      await api.post('/knowledge-base/categories', {
        name: newCategoryName,
        description: newCategoryDescription,
        color: '#3498db',
        icon: 'folder',
      });
      toast({ title: 'Category created successfully' });
      setCategoryOpen(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
      loadCategories();
    } catch {
      toast({ title: 'Failed to create category', variant: 'destructive' });
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || !editCategoryName) {
      toast({ title: 'Category name is required', variant: 'destructive' });
      return;
    }
    try {
      await api.put(`/knowledge-base/categories/${editingCategory.id}`, {
        name: editCategoryName,
        description: editCategoryDescription,
      });
      toast({ title: 'Category updated successfully' });
      setEditCategoryOpen(false);
      setEditingCategory(null);
      loadCategories();
    } catch {
      toast({ title: 'Failed to update category', variant: 'destructive' });
    }
  };

  const handleDeleteCategoryConfirm = async () => {
    if (!catToDelete) return;
    try {
      await api.delete(`/knowledge-base/categories/${catToDelete.id}`);
      toast({ title: 'Category deleted successfully' });
      loadCategories();
    } catch {
      toast({ title: 'Failed to delete category', variant: 'destructive' });
    } finally {
      setCatToDelete(null);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast({ title: 'Please enter a search query', variant: 'destructive' });
      return;
    }
    try {
      setSearching(true);
      const res = await api.post('/knowledge-base/search', {
        query: searchQuery,
        limit: 10,
        similarity_threshold: 0.7,
      });
      setSearchResults(res.data.results);
    } catch {
      toast({ title: 'Search failed', variant: 'destructive' });
    } finally {
      setSearching(false);
    }
  };

  const openEditCategory = (cat: KBCategory) => {
    setEditingCategory(cat);
    setEditCategoryName(cat.name);
    setEditCategoryDescription(cat.description || '');
    setEditCategoryOpen(true);
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Bot className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">Knowledge Base</h1>
            <Sparkles className="h-4 w-4 text-amber-400" />
          </div>
          <p className="text-sm text-muted-foreground">
            Manage your Co-Pilot knowledge base with semantic search capabilities
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="documents">
        <TabsList>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText className="h-4 w-4" /> Documents
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <Folder className="h-4 w-4" /> Categories
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart2 className="h-4 w-4" /> Analytics
          </TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Documents ({documents.length})
            </h2>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  setSearchOpen(true);
                }}
              >
                <Search className="h-4 w-4 mr-2" /> Search
              </Button>
              <Button onClick={() => setUploadOpen(true)}>
                <CloudUpload className="h-4 w-4 mr-2" /> Upload Document
              </Button>
            </div>
          </div>

          {loading && <Progress value={undefined} className="h-1" />}

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                      No documents uploaded yet
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          {doc.title}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {doc.file_name}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <StatusIcon status={doc.status} />
                          <Badge
                            variant="outline"
                            className={STATUS_BADGE[doc.status] ?? ''}
                          >
                            {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{formatFileSize(doc.file_size)}</TableCell>
                      <TableCell className="text-sm">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDocToDelete(doc)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Categories ({categories.length})
            </h2>
            <Button onClick={() => setCategoryOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create Category
            </Button>
          </div>

          {categories.length === 0 ? (
            <Card className="flex flex-col items-center justify-center py-16 text-center">
              <Folder className="h-12 w-12 text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground">No categories yet</p>
              <Button variant="outline" className="mt-4" onClick={() => setCategoryOpen(true)}>
                Create your first category
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((cat) => (
                <Card key={cat.id} className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundColor: cat.color + '20' }}
                      >
                        <Folder className="h-5 w-5" style={{ color: cat.color }} />
                      </div>
                      <CardTitle className="text-base">{cat.name}</CardTitle>
                    </div>
                    <CardAction>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditCategory(cat)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setCatToDelete(cat)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </CardAction>
                  </CardHeader>
                  {cat.description && (
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {cat.description}
                      </p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4 mt-4">
          <h2 className="text-lg font-semibold">Knowledge Base Analytics</h2>

          {analytics ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Card className="@container/card bg-gradient-to-t from-primary/5 to-card shadow-sm">
                <CardHeader>
                  <CardDescription>Indexed Documents</CardDescription>
                  <CardTitle className="text-3xl font-semibold tabular-nums">
                    {analytics.document_stats.indexed || 0}
                  </CardTitle>
                  <CardAction>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                  </CardAction>
                </CardHeader>
                <CardFooter className="text-xs text-muted-foreground">Ready for semantic search</CardFooter>
              </Card>

              <Card className="@container/card bg-gradient-to-t from-primary/5 to-card shadow-sm">
                <CardHeader>
                  <CardDescription>Knowledge Chunks</CardDescription>
                  <CardTitle className="text-3xl font-semibold tabular-nums">
                    {analytics.total_chunks}
                  </CardTitle>
                  <CardAction>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-500/10">
                      <Layers className="h-5 w-5 text-violet-500" />
                    </div>
                  </CardAction>
                </CardHeader>
                <CardFooter className="text-xs text-muted-foreground">Embedded text segments</CardFooter>
              </Card>

              <Card className="@container/card bg-gradient-to-t from-primary/5 to-card shadow-sm">
                <CardHeader>
                  <CardDescription>Storage Used</CardDescription>
                  <CardTitle className="text-3xl font-semibold tabular-nums">
                    {analytics.total_size_mb}
                    <span className="text-lg ml-1 font-normal text-muted-foreground">MB</span>
                  </CardTitle>
                  <CardAction>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10">
                      <HardDrive className="h-5 w-5 text-emerald-500" />
                    </div>
                  </CardAction>
                </CardHeader>
                <CardFooter className="text-xs text-muted-foreground">Total document size</CardFooter>
              </Card>

              <Card className="@container/card bg-gradient-to-t from-primary/5 to-card shadow-sm">
                <CardHeader>
                  <CardDescription>Processing</CardDescription>
                  <CardTitle className="text-3xl font-semibold tabular-nums">
                    {analytics.document_stats.processing || 0}
                  </CardTitle>
                  <CardAction>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
                      <TrendingUp className="h-5 w-5 text-amber-500" />
                    </div>
                  </CardAction>
                </CardHeader>
                <CardFooter className="text-xs text-muted-foreground">Documents being indexed</CardFooter>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center min-h-[200px]">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Upload Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="doc-title">Document Title</Label>
              <Input
                id="doc-title"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Enter a descriptive title"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="doc-file">File</Label>
              <input
                id="doc-file"
                type="file"
                accept=".pdf,.docx,.txt,.md"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium cursor-pointer"
              />
              <p className="text-xs text-muted-foreground">Supported: PDF, DOCX, TXT, MD (max 10 MB)</p>
            </div>
            {categories.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <Label>Categories (optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <label key={cat.id} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCategories((prev) => [...prev, cat.id]);
                          } else {
                            setSelectedCategories((prev) => prev.filter((id) => id !== cat.id));
                          }
                        }}
                        className="rounded border-input"
                      />
                      <span className="text-sm">{cat.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button
              onClick={handleFileUpload}
              disabled={!selectedFile || !uploadTitle || loading}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create Category Dialog ───────────────────────────────────────────── */}
      <Dialog open={categoryOpen} onOpenChange={setCategoryOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cat-name">Category Name</Label>
              <Input
                id="cat-name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g. Product Documentation"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cat-desc">Description (optional)</Label>
              <textarea
                id="cat-desc"
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                rows={3}
                placeholder="Brief description of this category…"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory} disabled={!newCategoryName}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Category Dialog ──────────────────────────────────────────────── */}
      <Dialog open={editCategoryOpen} onOpenChange={setEditCategoryOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-cat-name">Category Name</Label>
              <Input
                id="edit-cat-name"
                value={editCategoryName}
                onChange={(e) => setEditCategoryName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-cat-desc">Description (optional)</Label>
              <textarea
                id="edit-cat-desc"
                value={editCategoryDescription}
                onChange={(e) => setEditCategoryDescription(e.target.value)}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCategoryOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateCategory} disabled={!editCategoryName}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Search Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Search Knowledge Base</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter your search query…"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} disabled={searching}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>

          <div className="overflow-y-auto flex-1 space-y-3 pr-1">
            {searchResults.length > 0 && (
              <>
                <p className="text-sm text-muted-foreground">
                  {searchResults.length} results found
                </p>
                {searchResults.map((result, idx) => (
                  <Card key={`${result.chunk_id}-${idx}`} className="bg-muted/30">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-sm text-primary">{result.document_title}</p>
                        <Badge variant="outline" className="shrink-0 text-xs">
                          {(result.similarity_score * 100).toFixed(1)}% match
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{result.file_name}</p>
                      <p className="text-sm">{result.content}</p>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
            {searchResults.length === 0 && searchQuery && !searching && (
              <p className="text-center text-muted-foreground py-8">
                No results found. Try a different query.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSearchOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Document Confirm ───────────────────────────────────────────── */}
      <AlertDialog open={!!docToDelete} onOpenChange={(open) => !open && setDocToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{docToDelete?.title}</strong>? This will also
              remove all associated knowledge chunks. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDocToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteDocumentConfirm}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Delete Category Confirm ───────────────────────────────────────────── */}
      <AlertDialog open={!!catToDelete} onOpenChange={(open) => !open && setCatToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{catToDelete?.name}</strong>? This will also
              delete all associated articles. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCatToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteCategoryConfirm}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default KnowledgeBasePage;
